// Shell de la plataforma nueva: menú lateral (logo + botón para contraerlo, módulos del usuario con la navegación
// de cada uno en acordeón), el contenedor de scroll de las páginas, el panel lateral del asistente y los avisos
// (toasts). Sin barra superior en escritorio: idioma y tema van en el encabezado de cada página (PaginaModulo); en
// móvil queda una barra con el botón del cajón. Inventario y decisiones: docs/plataforma-nueva/aprendizajes/shell.md.
// Asistente (pedido del cliente, 2026-10-29): en escritorio es una COLUMNA fija a la derecha que empuja la página
// (no flota encima). Abre por defecto al entrar (sesión nueva) y, mientras está abierto, el menú se comprime a riel
// salvo que la persona lo expanda; al cerrarlo el menú vuelve a como ella lo dejó. En móvil sigue a pantalla completa.
// CONTRATOS: <nav aria-label="Módulos">, <main id="contenido"> (contenedor de scroll), botones de tema,
// useNavegacion().alNavegar() para las NavSecundaria, useNavegacion().asistente para abrirlo o preguntarle.
import './shell.css';
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, matchPath, useLocation, useNavigate } from 'react-router-dom';
import { Canvas } from '../../ds/components/core/Glass';
import { ToastViewport } from '../../ds/components/feedback/Toast';
import { Scrim } from '../../ds/components/overlays/Modal';
import { usePresence, useReducedMotion } from '../../ds/components/overlays/primitives';
import { branding } from '../../config/branding';
import useActivityTracker from '../../hooks/useActivityTracker';
import { MODULOS } from '../modulos/registro';
import { useConfigUsuario } from '../app/ConfigUsuario';
import { prepararGraficos, renderSinGpu } from '../app/graficos';
import { LimiteDeError } from '../comun/LimiteDeError';
import { NavegacionContext } from './navegacion';
import MenuLateral from './MenuLateral';
import Encabezado from './Encabezado';

const Burbuja = lazy(() => import('../modulos/burbuja/Burbuja'));
const ConfigurarMfa = lazy(() => import('../comun/ConfigurarMfa'));
const LLAVE_COLAPSADO = 'db-plataforma:menu-colapsado';
// Por sesión (no localStorage): cada entrada nueva arranca con el asistente abierto; dentro de la sesión se respeta
// si la persona lo cerró.
const LLAVE_ASISTENTE = 'db-plataforma:asistente';
// La de siempre cierra el menú al navegar con window.innerWidth < 768 (sidebarmenu.jsx:944).
const CONSULTA_MOVIL = '(max-width: 767.98px)';

export function moduloActivo(pathname) {
  return MODULOS.find((m) => m.rutas.some((r) => matchPath({ path: r.ruta, end: true }, pathname)));
}

/** Regla de la burbuja (App.js:665-679): nunca en el chat; `floatingChat` booleano decide; sin esa llave
 *  sigue a `chatAgent !== false`; y no antes de conocer las features. */
export function burbujaVisible(features, featuresListas, pathname) {
  if (!featuresListas) return false;
  if (pathname === '/chat' || pathname.startsWith('/chat/')) return false;
  return typeof features.floatingChat === 'boolean' ? features.floatingChat : features.chatAgent !== false;
}

function useEsMovil() {
  const [movil, setMovil] = useState(() => typeof window !== 'undefined' && window.matchMedia(CONSULTA_MOVIL).matches);
  useEffect(() => {
    const mq = window.matchMedia(CONSULTA_MOVIL);
    const alCambiar = () => setMovil(mq.matches);
    mq.addEventListener('change', alCambiar);
    return () => mq.removeEventListener('change', alCambiar);
  }, []);
  return movil;
}

function leerColapsado() {
  try { return localStorage.getItem(LLAVE_COLAPSADO) === '1'; } catch { return false; }
}

function asistenteInicial() {
  if (typeof window === 'undefined' || window.matchMedia(CONSULTA_MOVIL).matches) return false;
  try { return sessionStorage.getItem(LLAVE_ASISTENTE) !== 'cerrado'; } catch { return true; }
}

function recordarAsistente(abierto) {
  try { sessionStorage.setItem(LLAVE_ASISTENTE, abierto ? 'abierto' : 'cerrado'); } catch { /* sólo esta vista */ }
}

export default function Shell() {
  const { features, featuresListas, featuresDelServidor, username } = useConfigUsuario();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const esMovil = useEsMovil();
  const reducido = useReducedMotion();
  const [abierto, setAbierto] = useState(false);
  const [colapsado, setColapsado] = useState(leerColapsado);
  const [asistenteAbierto, setAsistenteAbierto] = useState(asistenteInicial);
  // La persona expandió el menú con el asistente abierto (se olvida al volver a abrir el asistente).
  const [menuConAsistente, setMenuConAsistente] = useState(false);
  const [mfa, setMfa] = useState(false);
  const [sinGpu, setSinGpu] = useState(renderSinGpu);
  const botonMenu = useRef(null);
  const panel = useRef(null);
  const activo = moduloActivo(pathname);
  const velo = usePresence(esMovil && abierto, 350);

  // Medición de la de siempre: page_view del módulo «navigation» al montar el menú (sidebarmenu.jsx:139).
  useActivityTracker(username, 'navigation');

  useEffect(() => {
    let vivo = true;
    prepararGraficos().then((v) => { if (vivo) setSinGpu(v); });
    return () => { vivo = false; };
  }, []);

  // Aterrizaje (App.js:455-465): una vez, con las features del servidor y sólo si entró a "/". Primero el del dominio;
  // si no aplica, el Inicio de quien lo tenga (después, «/» sigue siendo Monitoreo de Medios desde el menú).
  const aterrizado = useRef(false);
  useEffect(() => {
    if (!featuresDelServidor || aterrizado.current) return;
    aterrizado.current = true;
    if (pathname !== '/') return;
    const destino = branding.landing;
    if (destino && featuresDelServidor[destino.feature] === true) navigate(destino.path, { replace: true });
    else if (featuresDelServidor.inicio === true) navigate('/inicio', { replace: true });
  }, [featuresDelServidor, pathname, navigate]);

  // Al pasar a escritorio el cajón deja de existir; en móvil el asistente no arranca abierto (sería una pantalla
  // completa encima de la página).
  useEffect(() => {
    if (!esMovil) setAbierto(false);
    else setAsistenteAbierto(false);
  }, [esMovil]);

  const conBurbuja = burbujaVisible(features, featuresListas, pathname);
  // El asistente ocupa su columna (escritorio): mientras tanto el menú va comprimido salvo que la persona lo expanda.
  const asistenteEnColumna = conBurbuja && asistenteAbierto && !esMovil;
  const compacto = !esMovil && (asistenteEnColumna ? !menuConAsistente : colapsado);

  const abrirAsistente = useCallback(() => {
    setMenuConAsistente(false);
    setAsistenteAbierto(true);
    recordarAsistente(true);
  }, []);
  const cerrarAsistente = useCallback(() => {
    setAsistenteAbierto(false);
    recordarAsistente(false);
  }, []);

  const cerrar = useCallback(() => {
    setAbierto(false);
    // Después del render: mientras el cajón está abierto el encabezado es `inert` y no acepta el foco.
    requestAnimationFrame(() => botonMenu.current?.focus({ preventScroll: true }));
  }, []);

  // Cajón móvil abierto: el foco entra al menú y Esc lo cierra.
  useEffect(() => {
    if (!esMovil || !abierto) return undefined;
    const id = requestAnimationFrame(() => panel.current?.querySelector('a[href], button:not([aria-disabled="true"])')?.focus({ preventScroll: true }));
    const alTeclear = (e) => { if (e.key === 'Escape') cerrar(); };
    document.addEventListener('keydown', alTeclear);
    return () => { cancelAnimationFrame(id); document.removeEventListener('keydown', alTeclear); };
  }, [esMovil, abierto, cerrar]);

  const alternarMenu = useCallback(() => {
    if (esMovil) {
      setAbierto((v) => !v);
      return;
    }
    // Con el asistente abierto, expandir/contraer es temporal: la preferencia guardada es la de sin asistente.
    if (asistenteEnColumna) {
      setMenuConAsistente((v) => !v);
      return;
    }
    setColapsado((v) => {
      try { localStorage.setItem(LLAVE_COLAPSADO, v ? '0' : '1'); } catch { /* ignorar */ }
      return !v;
    });
  }, [esMovil, asistenteEnColumna]);

  const navegacion = useMemo(() => ({
    alNavegar: () => { if (esMovil) setAbierto(false); },
    esMovil,
    abierto,
    colapsado: compacto,
    abrir: () => {
      if (esMovil) setAbierto(true);
      else if (asistenteEnColumna) setMenuConAsistente(true);
      else setColapsado(false);
    },
    cerrar: () => setAbierto(false),
    alternar: alternarMenu,
    enShell: true,
    revelarTema: sinGpu === false,
    asistente: { disponible: conBurbuja, abierto: conBurbuja && asistenteAbierto, abrir: abrirAsistente, cerrar: cerrarAsistente },
  }), [esMovil, abierto, compacto, asistenteEnColumna, alternarMenu, sinGpu, conBurbuja, asistenteAbierto, abrirAsistente, cerrarAsistente]);

  return (
    <NavegacionContext.Provider value={navegacion}>
      <Canvas className="flex h-dvh overflow-hidden">
        <a
          href="#contenido"
          className="sr-only z-toast rounded-full bg-primary px-4 py-2 text-body-sm font-semibold text-primary-on focus:not-sr-only focus:fixed focus:left-3 focus:top-3"
        >
          Saltar al contenido
        </a>
        <MenuLateral
          activo={activo}
          compacto={compacto}
          esMovil={esMovil}
          abierto={abierto}
          onCerrar={cerrar}
          onAlternar={alternarMenu}
          onAbrirMfa={() => setMfa(true)}
          panelRef={panel}
        />
        {velo.mounted && (
          <div className="fixed inset-0 z-nav">
            <Scrim visible={velo.visible} reduced={reducido} onPointerDown={cerrar} />
          </div>
        )}
        <div
          className="flex min-w-0 flex-1 flex-col"
          {...(esMovil && abierto ? { inert: '' } : {})}
        >
          {esMovil && <Encabezado ref={botonMenu} abierto={abierto} onMenu={alternarMenu} />}
          <main id="contenido" tabIndex={-1} className="min-h-0 flex-1 overflow-y-auto">
            <Outlet />
          </main>
        </div>
        {/* display:contents: en escritorio el panel es una columna más del lienzo (empuja la página). */}
        {conBurbuja && (
          <div data-burbuja="" className="contents">
            <LimiteDeError modulo="floating_chat" compacto>
              <Suspense fallback={null}>
                <Burbuja abierto={asistenteAbierto} onAbrir={abrirAsistente} onCerrar={cerrarAsistente} />
              </Suspense>
            </LimiteDeError>
          </div>
        )}
        <ToastViewport placement="bottom-center" />
        {mfa && (
          <LimiteDeError modulo="app_root" compacto>
            <Suspense fallback={null}>
              <ConfigurarMfa open={mfa} onClose={() => setMfa(false)} />
            </Suspense>
          </LimiteDeError>
        )}
      </Canvas>
    </NavegacionContext.Provider>
  );
}
